import axios from '@/axios/config'

export default {
  namespaced: true,
  state: {
    plans: [],
    products: [],
    selectedPlan: null,
    planCoverages: [],
    protectionPolicies: [],
    selectedPolicy: null,
    policiesStats: {},
    searchResults: [],
    protectionClaims: [],
    selectedClaim: null,
    protectionPlans: [],
    selectedListingPlan: null,
    listingPlansStats: {},
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      perPage: 20,
    },
  },
  mutations: {
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setPlans(state, plans) {
      state.plans = plans
    },
    setProducts(state, products) {
      state.products = products
    },
    setSelectedPlan(state, plan) {
      state.selectedPlan = plan
    },
    setPlanCoverages(state, coverages) {
      state.planCoverages = coverages
    },
    setProtectionPolicies(state, policies) {
      state.protectionPolicies = policies
    },
    setSelectedPolicy(state, policy) {
      state.selectedPolicy = policy
    },
    setPoliciesStats(state, stats) {
      state.policiesStats = stats
    },
    setSearchResults(state, results) {
      state.searchResults = results
    },
    setProtectionClaims(state, claims) {
      state.protectionClaims = claims
    },
    setSelectedClaim(state, claim) {
      state.selectedClaim = claim
    },
    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination }
    },
    setProtectionPlans(state, plans) {
      state.protectionPlans = plans
    },
    setSelectedListingPlan(state, plan) {
      state.selectedListingPlan = plan
    },
    setListingPlansStats(state, stats) {
      state.listingPlansStats = stats
    },
    clearError(state) {
      state.error = null
    },
  },
  actions: {
    clearError({ commit }) {
      commit('clearError')
    },

    async fetchProtectionPolicies({ commit }, { page = 1, perPage = 20 } = {}) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.get('/api/protection-policies', {
          params: { page, per_page: perPage },
        })

        const policies =
          data?.payload?.data?.policies || data?.data?.policies || []
        const pagiInfo =
          data?.payload?.data?.pagi_info || data?.data?.pagi_info || {}

        // Convert pagi_info format to pagination format for consistency
        const pagination = {
          currentPage: pagiInfo.page || 1,
          totalCount: pagiInfo.count || 0,
          perPage: pagiInfo.per_page || 20,
          totalPages: Math.ceil(
            (pagiInfo.count || 0) / (pagiInfo.per_page || 20)
          ),
        }

        commit('setProtectionPolicies', policies)
        commit('setPagination', pagination)

        return { success: true, data: policies }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch protection policies'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error fetching protection policies:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchProtectionPolicy({ commit }, policyId) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.get(`/api/protection-policies/${policyId}`)

        const policy = data?.payload?.data?.policy || data?.data?.policy
        commit('setSelectedPolicy', policy)

        return { success: true, data: policy }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch protection policy'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error fetching protection policy:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async searchProtectionPolicies(
      { commit },
      { query = '', status = '', hasClaims = '', page = 1, perPage = 20 } = {}
    ) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const params = {
          page,
          per_page: perPage,
        }

        if (query) params.q = query
        if (status) params.status = status
        if (hasClaims) params.has_claims = hasClaims

        const { data } = await axios.get('/api/protection-policies/search', {
          params,
        })

        const policies =
          data?.payload?.data?.policies || data?.data?.policies || []
        const pagiInfo =
          data?.payload?.data?.pagi_info || data?.data?.pagi_info || {}

        // Convert pagi_info format to pagination format for consistency
        const pagination = {
          currentPage: pagiInfo.page || 1,
          totalCount: pagiInfo.count || 0,
          perPage: pagiInfo.per_page || 20,
          totalPages: Math.ceil(
            (pagiInfo.count || 0) / (pagiInfo.per_page || 20)
          ),
        }

        commit('setSearchResults', policies)
        commit('setPagination', pagination)

        return { success: true, data: policies }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to search protection policies'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error searching protection policies:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchProtectionPoliciesStats({ commit }) {
      try {
        const { data } = await axios.get('/api/protection-policies/stats')

        const stats = data?.payload?.data?.stats || data?.data?.stats || {}
        commit('setPoliciesStats', stats)

        return { success: true, data: stats }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch protection policies statistics'
        console.error('DamageProtection: Error fetching policies stats:', error)

        return { success: false, error: errorMessage }
      }
    },

    clearSelectedPlan({ commit }) {
      commit('setSelectedPlan', null)
      commit('setPlanCoverages', [])
    },

    clearSelectedPolicy({ commit }) {
      commit('setSelectedPolicy', null)
    },

    clearSearchResults({ commit }) {
      commit('setSearchResults', [])
    },

    // Protection Claims Actions
    async fetchProtectionClaims({ commit }, { page = 1, perPage = 20 } = {}) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.get('/api/protection-claims', {
          params: { page, per_page: perPage },
        })

        const claims = data?.payload?.data?.claims || data?.data?.claims || []
        const pagiInfo =
          data?.payload?.data?.pagi_info || data?.data?.pagi_info || {}

        const pagination = {
          currentPage: pagiInfo.page || 1,
          totalCount: pagiInfo.count || 0,
          perPage: pagiInfo.per_page || 20,
          totalPages: Math.ceil(
            (pagiInfo.count || 0) / (pagiInfo.per_page || 20)
          ),
        }

        commit('setProtectionClaims', claims)
        commit('setPagination', pagination)

        return { success: true, data: claims }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch protection claims'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error fetching protection claims:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchProtectionClaim({ commit }, claimId) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.get(`/api/protection-claims/${claimId}`)

        const claim = data?.payload?.data?.claim || data?.data?.claim
        commit('setSelectedClaim', claim)

        return { success: true, data: claim }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch protection claim'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error fetching protection claim:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async createProtectionClaim({ commit, dispatch }, { policyId, claimData }) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.post(
          `/api/protection-policies/${policyId}/claims`,
          {
            claim: claimData,
          }
        )

        const claim = data?.payload?.data?.claim || data?.data?.claim

        // Refresh the claims list
        await dispatch('fetchProtectionClaims')

        return { success: true, data: claim }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to create protection claim'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error creating protection claim:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    clearSelectedClaim({ commit }) {
      commit('setSelectedClaim', null)
    },

    // Listing Protection Plans Actions
    async fetchProtectionPlans({ commit }, { page = 1, perPage = 20 } = {}) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.get('/api/protection-plans', {
          params: { page, per_page: perPage },
        })

        const plans = data?.payload?.data?.protection_plans || []
        const pagiInfo = data?.payload?.data?.pagi_info || {}

        const pagination = {
          currentPage: pagiInfo.page || 1,
          totalCount: pagiInfo.count || 0,
          perPage: pagiInfo.per_page || 20,
          totalPages: Math.ceil(
            (pagiInfo.count || 0) / (pagiInfo.per_page || 20)
          ),
        }

        commit('setProtectionPlans', plans)
        commit('setPagination', pagination)

        return { success: true, data: plans }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || 'Failed to fetch protection plans'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error fetching protection plans:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async createProtectionPlan({ commit, dispatch }, planData) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.post('/api/protection-plans', {
          protection_plan: planData,
        })

        const plan = data?.payload?.data?.protection_plan

        // Refresh the list
        await dispatch('fetchProtectionPlans')

        return { success: true, data: plan }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || 'Failed to create protection plan'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error creating listing protection plan:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async updateProtectionPlan({ commit, dispatch }, { id, planData }) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const { data } = await axios.put(`/api/protection-plans/${id}`, {
          protection_plan: planData,
        })

        const plan = data?.payload?.data?.protection_plan

        // Refresh the list
        await dispatch('fetchProtectionPlans')

        return { success: true, data: plan }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Failed to update listing protection plan'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error updating listing protection plan:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async deleteProtectionPlan({ commit, dispatch }, planId) {
      commit('setLoading', true)
      commit('clearError')

      try {
        await axios.delete(`/api/protection-plans/${planId}`)

        // Refresh the list
        await dispatch('fetchProtectionPlans')

        return { success: true }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Failed to delete listing protection plan'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error deleting listing protection plan:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchProtectionPlansStats({ commit }) {
      try {
        const { data } = await axios.get('/api/protection-plans/stats')

        const stats = data?.payload?.data?.stats || {}
        commit('setListingPlansStats', stats)

        return { success: true, data: stats }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Failed to fetch protection plans statistics'
        console.error(
          'DamageProtection: Error fetching listing plans stats:',
          error
        )

        return { success: false, error: errorMessage }
      }
    },

    async decisionEngine({ commit }, { planId, contentsLimit } = {}) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const params = {}
        if (contentsLimit) {
          params.contents_limit = contentsLimit
        }

        const { data } = await axios.get(
          `/api/protection-plans/${planId}/decision-engine`,
          { params }
        )

        const decisionEngine =
          data?.payload?.data?.decision_engine || data?.data?.decision_engine

        return { success: true, data: decisionEngine }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || 'Failed to calculate decision engine'
        commit('setError', errorMessage)
        console.error(
          'DamageProtection: Error calculating decision engine:',
          error
        )

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    async issuePolicy(
      { commit },
      { planId, effectiveDate, termEndDate, subscribedQuoteId, termsAccepted }
    ) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const params = {}

        // Add parameters if provided
        if (effectiveDate) params.effective_date = effectiveDate
        if (termEndDate) params.term_end_date = termEndDate
        if (subscribedQuoteId) params.subscribed_quote_id = subscribedQuoteId
        if (termsAccepted) params.terms_accepted = termsAccepted

        const { data } = await axios.post(
          `/api/protection-plans/${planId}/issue-policy`,
          params
        )

        const result = data?.payload?.data || data?.data

        return { success: true, data: result }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || 'Failed to issue policy'
        commit('setError', errorMessage)
        console.error('DamageProtection: Error issuing policy:', error)

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    clearSelectedListingPlan({ commit }) {
      commit('setSelectedListingPlan', null)
    },
  },
  getters: {
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    allPlans: state => state.plans,
    allProducts: state => state.products,
    selectedPlan: state => state.selectedPlan,
    planCoverages: state => state.planCoverages,
    plansCount: state => state.plans.length,
    productsCount: state => state.products.length,
    protectionPolicies: state => state.protectionPolicies,
    selectedPolicy: state => state.selectedPolicy,
    policiesStats: state => state.policiesStats,
    searchResults: state => state.searchResults,
    pagination: state => state.pagination,
    policiesCount: state => state.protectionPolicies.length,
    hasActivePolicies: state => state.protectionPolicies.some(p => p.active),
    hasClaimsCount: state =>
      state.protectionPolicies.filter(p => p.has_claims).length,
    protectionPlans: state => state.protectionPlans,
    selectedListingPlan: state => state.selectedListingPlan,
    listingPlansStats: state => state.listingPlansStats,
    listingPlansCount: state => state.protectionPlans.length,
    hasActiveListingPlans: state => state.protectionPlans.some(p => p.active),
  },
}
